import * as React from 'react';
import color from 'color';
import lodashSplit from 'lodash/split';
import Tooltip from '../Tooltip/Tooltip';
import cn from '~modules/cn';

import './avatar.scss';

export type AvatarProps = {
    nameOrEmail?: string;
    tipLabel?: string;
    multipleTipDivider?: string;
    className?: string;
    icon?: React.ReactNode;
    size?: 'x-large' | 'large' | 'medium' | 'small' | 'x-small';
    color?: 'default' | 'light' | 'white' | 'primary';
    shape?: 'round' | 'square';
    useInitials?: boolean;
    randomColor?: boolean;
    dataTip?: string;
};

export const defaultProps: AvatarProps = {
    size: 'medium',
    shape: 'round'
};

const getInitials = (nameOrEmail: string): string => {
    const nameOrEmailNamePart = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
    const nameParts = (nameOrEmailNamePart?.includes(' ') ? lodashSplit(nameOrEmailNamePart, ' ', 2) : nameOrEmailNamePart?.split('.')) ?? [''];
    return nameParts.map((part) => lodashSplit(part, '', 1)[0]?.toUpperCase()).join('');
};

const memoGetHashNumberFromString = {} as Record<string, number>;
const getHashNumberFromString = (str: string): number => {
    if (memoGetHashNumberFromString[str]) return memoGetHashNumberFromString[str]!;

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    hash = Math.abs(hash);

    memoGetHashNumberFromString[str] = hash;
    return hash;
};

const normalizeHash = (hash: number, min: number, max: number): number => Math.floor((hash % (max - min)) + min);

const hueRange = [
    // #0ACF83
    {
        h: [120, 160],
        s: [55, 95],
        l: [25, 55]
    },
    // #4AAAC1
    {
        h: [170, 220],
        s: [30, 70],
        l: [30, 70]
    },
    // #F3980D
    {
        h: [30, 40],
        s: [75, 95],
        l: [45, 65]
    },
    // #F463E5
    {
        h: [290, 340],
        s: [70, 90],
        l: [50, 90]
    }
] as const;

type HUERangeType = (typeof hueRange)[number];

const getHSLRange = (hash: number): HUERangeType => {
    const hueRangeIndex = hash % hueRange.length;
    const value = hueRange[hueRangeIndex];
    return value ?? hueRange[0];
};

const generateColor = (name: string) => {
    const hash = getHashNumberFromString(name);
    const randomRange = getHSLRange(hash);
    const h = normalizeHash(hash, randomRange.h[0], randomRange.h[1]);
    const s = normalizeHash(hash, randomRange.s[0], randomRange.s[1]);
    const l = normalizeHash(hash, randomRange.l[0], randomRange.l[1]);
    return color({ h, s, l }).hex();
};

const RenderInfoText: React.FC<{ text?: string; textDivider?: string; label?: string }> = ({ text, textDivider, label }) => {
    if (!text) {
        return null;
    }

    if (!textDivider) {
        return (
            <>
                {text} {label}
            </>
        );
    }

    const textParts = text.split(textDivider);
    return (
        <>
            <div>
                {textParts.map((part, index) => (
                    <div key={index}>{part}</div>
                ))}
            </div>
            {label}
        </>
    );
};

export const Avatar = (props: AvatarProps): JSX.Element => {
    const {
        nameOrEmail = '',
        tipLabel,
        multipleTipDivider,
        icon,
        className,
        dataTip,
        size = 'medium',
        color: colorType,
        shape,
        randomColor,
        useInitials = true
    } = {
        ...defaultProps,
        ...props
    };
    const ref = React.useRef<HTMLDivElement>(null);
    const label = useInitials ? getInitials(nameOrEmail) : nameOrEmail;
    const generatedColor = randomColor ? generateColor(nameOrEmail) : 'inherit';
    let randomColorClassName = '';
    if (randomColor) {
        if (color(generatedColor).isDark()) {
            randomColorClassName = 'avatar--random-dark-bg-text-color';
        } else {
            randomColorClassName = 'avatar--random-light-bg-text-color';
        }
    }

    return (
        <div
            className={cn(
                'avatar',
                `avatar--${size}`,
                `avatar--${shape}`,
                {
                    [`avatar--${colorType}`]: !!colorType,
                    'avatar--with-hover': !!dataTip
                },
                randomColorClassName,
                className
            )}
            {...(randomColor ? { style: { backgroundColor: generatedColor } } : {})}
            ref={ref}
        >
            {icon ? icon : label}
            {dataTip && (
                <Tooltip
                    className="avatar__tooltip"
                    appearanceClassNames={['tooltip--box-shadow', 'tooltip--default-max-height']}
                    for={ref}
                    tooltipProps={{
                        delayShow: 300,
                        trigger: 'hover'
                    }}
                >
                    <RenderInfoText text={dataTip} label={tipLabel} textDivider={multipleTipDivider} />
                </Tooltip>
            )}
        </div>
    );
};

export default Avatar;
